import { checkDate } from 'services/utils/validations'
import { isStrongPasswordEnabled } from 'services/utils/feature-flags'
import {
  getCountryCode,
  countriesWithoutPostcode,
} from '../../utils/countries'
import { validateName } from './index'
import {
  emailRegex,
  phoneNumberRegex,
  postcodeRegex,
  walmartNumberRegex,
  colleagueDiscountNumberRegex,
  passwordRegex,
} from './validation-regex'

export const titleValidationError = (input) => {
  if (!input) return `Choose your title`
  return ''
}

export const nameValidationRegex = (value, field = 'value') => {
  const hasNumber = /\d/.test(value)
  const hasLengthMoreThan30 = value.length > 30
  const hasNotAllowedSpecialCharacters = !/^[A-Za-z0-9\-']*$/.test(value)
  if (
    (hasLengthMoreThan30 && hasNumber) ||
    (hasLengthMoreThan30 && hasNotAllowedSpecialCharacters) ||
    (hasNumber && hasNotAllowedSpecialCharacters)
  ) {
    return `Your ${field} must not exceed 30 characters or contain numbers or special characters, except for a dash or an apostrophe`
  }
  if (hasLengthMoreThan30) { return `Your ${field} must not exceed 30 characters` }
  if (hasNumber) return 'You must not use numbers'
  if (hasNotAllowedSpecialCharacters) { return 'You must not use special characters other than a dash or an apostrophe' }
  return ''
}

export const firstNameValidationRegex = name => nameValidationRegex(name, 'first name')
export const lastNameValidationRegex = name => nameValidationRegex(name, 'last name')

export const firstNameValidationError = (input) => {
  if (!input) return 'Enter your first name'
  return firstNameValidationRegex(input)
}

export const lastNameValidationError = (input) => {
  if (!input) return 'Enter your last name'
  return lastNameValidationRegex(input)
}

export const emailValidationError = (input) => {
  if (!input) return 'Enter your email address'
  if (!emailRegex(input)) return 'We do not recognise that email address'
  return ''
}

export const passwordValidationError = (input) => {
  if (!input) return 'Enter your password'
  return ''
}

export const newPasswordValidationError = (input, oldPassword = '') => {
  if (!input) return 'Create a password'
  if (input === oldPassword) return 'Must be different from current password'
  if (!isStrongPasswordEnabled && !passwordRegex(input)) { return 'Your password must be at least 8 characters' }
  return ''
}

export const newWeakPasswordValidationError = (input, oldPassword = '') => {
  if (!input) return 'Create a password'
  if (input === oldPassword) return 'Must be different from current password'
  if (!passwordRegex(input)) { return 'Your password must be at least 8 characters' }
  return ''
}

export const phoneValidationError = (input, countryCode) => {
  if (!input) return 'Enter your phone number'
  if (!phoneNumberRegex(input, countryCode)) { return 'We do not recognise that phone number' }
  return ''
}

export const ukMobileValidationError = (input, countryCode) => {
  if (!input) return 'Enter a UK mobile number'
  if (countryCode !== '+44') return 'We do not recognise that UK mobile number'
  if (!input.startsWith('07')) { return 'We do not recognise that UK mobile number' }
  if (!phoneNumberRegex(input, '+44')) { return 'We do not recognise that UK mobile number' }
  return ''
}

export const postcodeValidationError = (input, country = 'United Kingdom') => {
  const countryCode = getCountryCode(country)
  const needPostcode = !countriesWithoutPostcode.find(countryInList => countryInList === country)
  if (!input && needPostcode) return 'Enter your postcode'
  if (countryCode === 'GB' && !postcodeRegex(input)) { return 'We do not recognise that postcode' }
  return ''
}

export const addressLineValidationError = (input, lineNumber = 1) => {
  if (lineNumber === 1 && !input) return 'Enter an address line 1'
  if (input && !/^[a-z0-9&'*)(,#.\-'\s]+$/i.test(input)) { return `Use only letters, numbers and &'*(),#.-' in your address` }
  return ''
}

export const cityValidationError = (input) => {
  if (!input) return 'Enter a city or town'
  return ''
}

export const countryValidationError = (input) => {
  if (!input) return 'Enter a country'
  return ''
}

export const walmartNumberValidationError = (input) => {
  if (!input) return 'Enter a Walmart number'
  if (!walmartNumberRegex(input)) { return 'We do not recognise that Walmart number' }
  return ''
}

export const colleagueDiscountNumberValidationError = (input) => {
  if (!input) return 'Enter a Colleague Discount card number'
  if (!colleagueDiscountNumberRegex(input)) { return 'We do not recognise that Colleague Discount card number' }
  return ''
}

export const expiryDateValidation = (value) => {
  const { dateInvalid, datePast } = checkDate(value)
  if (!value) return 'Enter expiry date'
  if (datePast) return 'Card has expired'
  if (dateInvalid) return 'Enter as MM/YY'
  return ''
}

export const validateNameAndNickname = (value, field = 'value') => {
  const hasNumber = /\d/.test(value)
  const hasLengthMoreThan30 = value.length > 30
  const hasNotAllowedSpecialCharacters = !/^[A-Za-z0-9\-'\s]*$/.test(value)
  const hasOnlySpaces = /^[\s]*$/.test(value)
  if (
    (hasLengthMoreThan30 && hasNumber) ||
    (hasLengthMoreThan30 && hasNotAllowedSpecialCharacters) ||
    (hasNumber && hasNotAllowedSpecialCharacters) 
  ) {
    return `Your ${field} must not exceed 30 characters or contain numbers or special characters, except for a dash or an apostrophe`
  }
  if (hasLengthMoreThan30) { return `Your ${field} must not exceed 30 characters` }
  if (hasNumber) return 'You must not use numbers'
  if (hasOnlySpaces) return 'You must not use special characters other than a dash or an apostrophe'
  if (hasNotAllowedSpecialCharacters) { return 'You must not use special characters other than a dash or an apostrophe' }
  return ''
}

export const nameOnCardValidation = (value) => {
  if (!validateName(value)) return 'We do not recognise that name'
  return ''
}

// export const nameOnCardValidation = (value) => {
//   if(value) return validateNameAndNickname(value, "name")
//   return 'Name cannot be empty'
// }

// export const nickNameOnCardValidation = (value) => {
//   if(value) return validateNameAndNickname(value, "nickname")
//   return ''
// }

export const cardNumberValidationError = (input) => {
  if (!input) return 'We do not recognise that card number'
  return ''
}

export const addressSelectListValidation = (value) => {
  if (value === 'Choose...') return 'Choose or add billing address'
  return ''
}

export const termsValidationError = (value) => {
  if (!value) return 'Accept terms and conditions to continue'
  return ''
}

export const otpValidationError = (value) => {
  if (!value) return 'Please enter your code'
  return ''
}

export const isValidPost = (value) => {
  if (!value || value === 'Choose...') return 'Choose or add address'
  if (value === 'Add a new address') return 'Choose address'
  return ''
}

export const isValidSms = (value) => {
  if (!value || value === 'Choose...') return 'Choose or add phone number'
  return ''
}

export const isValidDeliveryInstruction = (value) => {
  if (value.length >= 1000) { return 'The delivery instructions should be less than 1000 characters. Please make it shorter.' }
  return ''
}
